import React, {useState} from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {formatDate} from '../helpers/UtilityHelper'
import {useMemo} from 'react'
import {subDays} from 'date-fns'
import {CustomPopover} from '../components/weight/list/SideDrawer'
import {FONT, SIZE, WEIGHT} from '../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS} from '../styles/foundation/colors'
import {SHADOW} from '../styles/foundation/effects'

import LaunchIcon from '@mui/icons-material/Launch'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'

const NotificationDetails = ({notificationList}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // notification Hub calculation
  const pushNotification = useMemo(() => {
    const fourteenDaysAgo = subDays(new Date(), 14)
    return notificationList?.filter((val) => {
      const createdDate = new Date(val.createdDate)
      return val.destination === 'PUSH' && createdDate >= fourteenDaysAgo
    })
  }, [notificationList])

  const handleOpen = () => {
    setSidebarOpen(true)
  }

  return (
    <>
      <Tooltip title='Updates'>
        <IconButton
          sx={{
            color: '#637381',
            borderRadius: '10px',
            '&:hover': {bgcolor: 'rgba(99,115,129,0.10)', color: '#212B36'},
          }}
          onClick={() => handleOpen()}
          size='small'
        >
          <Badge badgeContent={pushNotification?.length} color='error'>
            <NotificationsNoneOutlinedIcon sx={{fontSize: 22}} />
          </Badge>
        </IconButton>
      </Tooltip>
      <CustomPopover
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        subtitle='Recent Notifications (Last 14 Days)'
        popoverWidth='30%'
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        children={<PushNotification pushNotification={pushNotification} />}
      />
    </>
  )
}

export default NotificationDetails

const PushNotification = React.memo(({pushNotification}) => {
  const [expanded, setExpanded] = useState({})
  const handleExpandClick = (val) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [val.id]: !prevExpanded[val.id],
    }))
  }
  return (
    <>
      {pushNotification?.length > 0 ? (
        pushNotification.map((val) => {
          const isExpanded = expanded[val.id]

          return (
            <Card
              key={val.id}
              elevation={0}
              sx={{
                mb: 1.5,
                boxShadow: SHADOW.card,
                borderRadius: '10px',
                border: `1px solid ${BORDER}`,
                backgroundColor: WHITE,
                overflow: 'hidden',
              }}
            >
              {/* Header: status dot + title + date */}
              <Box sx={{px: 2, pt: 1.75, pb: 0.75, display: 'flex', alignItems: 'center', gap: 1}}>
                <Box sx={{width: 8, height: 8, bgcolor: SUCCESS, borderRadius: '50%', flexShrink: 0}} />
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: FONT,
                    fontSize: SIZE.sm,
                    fontWeight: WEIGHT.bold,
                    color: NAVY,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={val.header}
                >
                  {val.header}
                </Typography>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, flexShrink: 0}}>
                  {formatDate(val.createdDate, 'dd MMM, yyyy')}
                </Typography>
              </Box>

              {/* Body */}
              <Box sx={{px: 2, pb: 1.75}}>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE.xs,
                    color: SUBTLE,
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: isExpanded ? 'unset' : 5,
                  }}
                >
                  {val.text}
                </Typography>

                {val.text.length > 150 && (
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => handleExpandClick(val)}
                    sx={{mt: 0.5, px: 0, minWidth: 0, textTransform: 'none', fontFamily: FONT, fontWeight: WEIGHT.semibold, color: PRIMARY_BLUE}}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
                {val.text1 && val.description && (
                  <Box sx={{mt: 1.5}}>
                    <Tooltip title={val.text1}>
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => window.open(val.description, '_blank')}
                        endIcon={<LaunchIcon />}
                        sx={{textTransform: 'none', fontFamily: FONT, fontWeight: WEIGHT.semibold, borderRadius: '8px', boxShadow: 'none', '&:hover': {boxShadow: 'none'}}}
                      >
                        View
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Card>
          )
        })
      ) : (
        <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' sx={{minHeight: '30vh', textAlign: 'center'}}>
          <NotificationsNoneOutlinedIcon sx={{fontSize: 40, color: BORDER, mb: 1}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>
            No new notifications in the last 14 days.
          </Typography>
        </Box>
      )}
    </>
  )
})
